import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
// Types
import QuestionTypes from 'types/QuestionTypes';
import TimerTypes from 'types/TimerTypes';
// Models
import IQuestion from 'models/Question';
import ITimer from 'models/Timer';
// Initial form
import { initialQuizForm } from 'store/form/formInitial';

/**
 * Shared transforms between the DB-shaped quiz object (`IQuiz` / draftQuiz sent
 * by the agent / payload accepted by createQuiz / updateQuiz) and the
 * react-hook-form values used inside QuizFormPage and QuizAgentDialog.
 *
 * DB shape examples:
 *   prizes:                    string[]
 *   spotPrizes:                string[]
 *   registrationPage.showPriorQuiz: boolean
 *   registrationPage.fields:  { ref, title, type, required, binding?, choices? }[]
 *   integrations:              { _id, name, service }[] (populated)
 *   startsDate / endsDate:     string (ms timestamp)
 *   timers[].time (Absolute):  string (ms timestamp)
 *   questions[].config.items[].correctAnswers (TopList):   string[]
 *   questions[].config.correctAnswers (ShortText):         string[]
 *
 * Form shape mirrors what the existing controllers in QuizFormPage expect:
 *   prizes:                    { prize: string }[]
 *   spotPrizes:                string (single value)
 *   registrationPage.showPriorQuiz: boolean (RegistrationForm.tsx's Select converts
 *                                    to/from 'true'/'false' locally, at the native
 *                                    <select> - that string never enters form state)
 *   registrationPage.fields:  { ref, title, type, required, binding?, choices? }[]
 *   integrations:              { integrationId: string, value: true }[]
 *   startsDate / endsDate:     Date | ''
 *   timers[].time (Absolute):  Date
 *   questions[].config.items[].correctAnswers (TopList):   { title: string }[]
 *   questions[].config.correctAnswers (ShortText):         { title: string }[]
 */

const isPlainObject = (value: any): value is Record<string, any> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const mapQuestionToForm = (question: IQuestion | any) => {
  if (!question) return question;

  if (question.type === QuestionTypes.TopList && question.config?.items) {
    return {
      ...question,
      score: question.score ?? '',
      config: {
        ...(question.config || {}),
        items: question.config.items.map((item: any) => ({
          ...item,
          correctAnswers: Array.isArray(item?.correctAnswers)
            ? item.correctAnswers.map((answer: any) =>
                typeof answer === 'string' ? { title: answer } : answer
              )
            : [],
        })),
      },
    };
  }

  if (question.type === QuestionTypes.ShortText && question.config?.correctAnswers) {
    return {
      ...question,
      score: question.score ?? '',
      config: {
        ...question.config,
        correctAnswers: Array.isArray(question.config.correctAnswers)
          ? question.config.correctAnswers.map((answer: any) =>
              typeof answer === 'string' ? { title: answer } : answer
            )
          : [],
      },
    };
  }

  // Normalize score for all other question types so RHF always has a defined
  // value — avoids uncontrolled-input warnings when the agent/draft omits it.
  return { ...question, score: question.score ?? '' };
};

const mapQuestionFromForm = (question: any) => {
  if (!question) return question;

  if (question.type === QuestionTypes.TopList && question.config?.items) {
    return {
      ...question,
      config: {
        ...(question.config || {}),
        items: question.config.items.map((item: any) => ({
          ...item,
          correctAnswers: Array.isArray(item?.correctAnswers)
            ? item.correctAnswers.map((answer: any) =>
                typeof answer === 'string' ? answer : answer?.title
              ).filter((value: any) => value !== undefined && value !== null)
            : [],
        })),
      },
    };
  }

  if (question.type === QuestionTypes.ShortText && question.config?.correctAnswers) {
    return {
      ...question,
      config: {
        ...question.config,
        correctAnswers: Array.isArray(question.config.correctAnswers)
          ? question.config.correctAnswers.map((answer: any) =>
              typeof answer === 'string' ? answer : answer?.title
            ).filter((value: any) => value !== undefined && value !== null)
          : [],
      },
    };
  }

  return question;
};

/**
 * Convert a DB-shaped quiz (or partial quiz, e.g. draftQuiz from the agent)
 * into the form-shaped values consumed by react-hook-form.
 *
 * Safe to call with `null` / `undefined` / partial input; missing fields will
 * default to `initialQuizForm` values where defined and otherwise be omitted.
 */
export const quizToFormValues = (quiz: any) => {
  const source: any = quiz || {};
  const base: any = { ...initialQuizForm, ...source };

  const namespaceId = isPlainObject(source.namespace)
    ? source.namespace._id
    : (source.namespaceId || source.namespace || '');

  const quizGroup = isPlainObject(source.quizGroup)
    ? source.quizGroup._id
    : (source.quizGroup || '');

  const formValues: any = {
    ...base,
    nonce: uuidv4(),
    namespaceId,
    quizGroup,
    startsDate: base.startsDate ? new Date(Number(base.startsDate)) : '',
    endsDate: base.endsDate ? new Date(Number(base.endsDate)) : '',
    spotPrizes: Array.isArray(source.spotPrizes)
      ? source.spotPrizes.join('')
      : (source.spotPrizes || ''),
    prizes: Array.isArray(source.prizes)
      ? source.prizes.map((prize: any) =>
          typeof prize === 'string' ? { prize } : prize
        )
      : [],
    integrations: Array.isArray(source.integrations)
      ? source.integrations.map((integration: any) =>
          typeof integration === 'string'
            ? { integrationId: integration, value: true }
            : { integrationId: integration?._id, value: true }
        )
      : [],
    timers: Array.isArray(base.timers)
      ? base.timers.map((timer: ITimer) =>
          timer?.type === TimerTypes.Absolute && timer.time
            ? { ...timer, time: new Date(Number(timer.time)) }
            : timer
        )
      : [],
    callToAction: base.callToAction
      ? { ...initialQuizForm.callToAction, ...base.callToAction }
      : initialQuizForm.callToAction,
    // null means the registration page is explicitly disabled; only fall back
    // to the form default when the value is absent (undefined).
    // `showPriorQuiz` stays a real boolean here - the register-screen Select
    // (RegistrationForm.tsx) converts to/from 'true'/'false' locally, right at
    // the native <select>, so that string never leaks into shared form state.
    registrationPage: source.registrationPage != null
      ? {
          ...initialQuizForm.registrationPage,
          ...source.registrationPage,
          showPriorQuiz: source.registrationPage.showPriorQuiz ?? initialQuizForm.registrationPage.showPriorQuiz,
          fields: Array.isArray(source.registrationPage.fields) ? source.registrationPage.fields : [],
        }
      : source.registrationPage === null ? null : initialQuizForm.registrationPage,
    questions: Array.isArray(base.questions)
      ? base.questions.map((question: IQuestion) => mapQuestionToForm(question))
      : [],
  };

  return formValues;
};

/**
 * Convert form-shaped values back into the DB-shaped quiz object accepted by
 * the API (createQuiz / updateQuiz) and the quiz agent (createThread /
 * streamMessage `draftQuiz` payload).
 *
 * This intentionally keeps the question payload shape as-is for non
 * TopList/ShortText types — additional per-quiz-type massaging (e.g. Streak /
 * Personality / Ranking config rewrites) is still applied at submit time in
 * QuizFormPage and QuizAgentFinalizeDialog.
 */
export const formValuesToQuiz = (formValues: any): any => {
  const source: any = formValues || {};

  const { namespaceId, namespace, ...rest } = source;

  // A plain-string `namespace` that differs from `namespaceId` means the user
  // changed the selector after the form was initialised — honour that choice
  // instead of keeping the stale `namespaceId`. When `namespace` is a
  // populated object (initial load from DB) the resolved `namespaceId` wins.
  const namespaceValue =
    typeof namespace === 'string' && namespace && namespace !== namespaceId
      ? namespace
      : namespaceId || (isPlainObject(namespace) ? namespace._id : namespace) || '';

  const result: any = {
    ...rest,
    namespace: namespaceValue,
    startsDate: source.startsDate ? moment(source.startsDate).format('x') : '',
    endsDate: source.endsDate ? moment(source.endsDate).format('x') : '',
    spotPrizes: source.spotPrizes
      ? (Array.isArray(source.spotPrizes) ? source.spotPrizes : [source.spotPrizes])
      : [],
    prizes: Array.isArray(source.prizes)
      ? source.prizes
          .map((entry: any) => (typeof entry === 'string' ? entry : entry?.prize))
          .filter((value: any) => value !== undefined && value !== null)
      : [],
    integrations: Array.isArray(source.integrations)
      ? source.integrations.reduce((acc: string[], entry: any) => {
          if (typeof entry === 'string') {
            acc.push(entry);
          } else if (entry?.value && entry.integrationId) {
            acc.push(entry.integrationId);
          }
          return acc;
        }, [])
      : [],
    timers: Array.isArray(source.timers)
      ? source.timers.map((timer: ITimer) =>
          timer?.type === TimerTypes.Absolute && timer.time
            ? { ...timer, time: moment(timer.time).format('x') }
            : timer
        )
      : [],
    registrationPage: source.registrationPage
      ? {
          ...source.registrationPage,
          showPriorQuiz:
            typeof source.registrationPage.showPriorQuiz === 'string'
              ? source.registrationPage.showPriorQuiz === 'true'
              : !!source.registrationPage.showPriorQuiz,
        }
      : null,
    questions: Array.isArray(source.questions)
      ? source.questions.map((question: any) => mapQuestionFromForm(question))
      : [],
  };

  return result;
};
